import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  firstValueFrom,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';
import {
  SupplyActionLogDto,
  SupplyActionLogFilterOption,
  SupplyActionLogFilters,
  SupplyActionLogListResult,
  SupplyDocumentDto,
  SupplyDocumentFileDto,
  SupplyDocumentGridDto,
  SupplyDocumentListResult,
  SupplyDocumentWrite,
  SupplyRecordDownload,
} from './supply-records-api.types';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const SUPPLIES_PATH = '/catalogs/supplies/supplies';

@Injectable({ providedIn: 'root' })
export class SupplyRecordsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${SUPPLIES_PATH}`;

  listDocuments(supplyUuid: string): Promise<SupplyDocumentListResult> {
    return this.safeGet(
      this.documentsUrl(supplyUuid),
      listParams('createdAt'),
      unwrapDocumentList,
    );
  }

  getDocument(supplyUuid: string, documentUuid: string): Promise<SupplyDocumentDto> {
    return this.safeGet(
      `${this.documentsUrl(supplyUuid)}/${encodeURIComponent(documentUuid)}`,
      new HttpParams(),
      unwrapDocument,
    );
  }

  createDocument(supplyUuid: string, write: SupplyDocumentWrite): Promise<SupplyDocumentDto> {
    return this.mutate(
      this.http.post<unknown>(this.documentsUrl(supplyUuid), toFormData(write)),
      unwrapDocument,
    );
  }

  updateDocument(
    supplyUuid: string,
    documentUuid: string,
    write: SupplyDocumentWrite,
  ): Promise<SupplyDocumentDto> {
    return this.mutate(
      this.http.put<unknown>(
        `${this.documentsUrl(supplyUuid)}/${encodeURIComponent(documentUuid)}`,
        toFormData(write),
      ),
      unwrapDocument,
    );
  }

  removeDocument(supplyUuid: string, documentUuid: string): Promise<void> {
    return this.mutate(
      this.http.delete<unknown>(
        `${this.documentsUrl(supplyUuid)}/${encodeURIComponent(documentUuid)}`,
      ),
      () => undefined,
    );
  }

  removeDocumentFile(
    supplyUuid: string,
    documentUuid: string,
    fileUuid: string,
  ): Promise<void> {
    return this.mutate(
      this.http.delete<unknown>(
        `${this.documentsUrl(supplyUuid)}/${encodeURIComponent(documentUuid)}/files/${encodeURIComponent(fileUuid)}`,
      ),
      () => undefined,
    );
  }

  exportDocuments(supplyUuid: string): Promise<SupplyRecordDownload> {
    return this.download(
      `${this.documentsUrl(supplyUuid)}/excel`,
      exportParams('createdAt'),
      'documentos-insumo.xlsx',
    );
  }

  listActionLog(supplyUuid: string): Promise<SupplyActionLogListResult> {
    return this.safeGet(
      this.actionLogUrl(supplyUuid),
      listParams('dateTime'),
      unwrapActionLogList,
    );
  }

  actionLogFilters(supplyUuid: string): Promise<SupplyActionLogFilters> {
    return this.safeGet(
      `${this.actionLogUrl(supplyUuid)}/filter-selects`,
      new HttpParams(),
      unwrapActionLogFilters,
    );
  }

  exportActionLog(supplyUuid: string): Promise<SupplyRecordDownload> {
    return this.download(
      `${this.actionLogUrl(supplyUuid)}/excel`,
      exportParams('dateTime'),
      'bitacora-insumo.xlsx',
    );
  }

  private documentsUrl(supplyUuid: string): string {
    return `${this.baseUrl}/${encodeURIComponent(supplyUuid)}/documents`;
  }

  private actionLogUrl(supplyUuid: string): string {
    return `${this.baseUrl}/${encodeURIComponent(supplyUuid)}/action-log`;
  }

  private safeGet<T>(
    url: string,
    params: HttpParams,
    project: (body: unknown) => T,
  ): Promise<T> {
    return this.request(
      this.http
        .get<unknown>(url, { params })
        .pipe(retry({ count: 1, delay: error => retryDelay(error) })),
      project,
    );
  }

  private mutate<T>(source: Observable<unknown>, project: (body: unknown) => T): Promise<T> {
    return this.request(source, project);
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapSettingsError(error);
    }
  }

  private async download(
    url: string,
    params: HttpParams,
    fallbackFilename: string,
  ): Promise<SupplyRecordDownload> {
    try {
      const response = await firstValueFrom(
        this.http
          .get(url, { params, observe: 'response', responseType: 'blob' })
          .pipe(
            timeout(REQUEST_TIMEOUT_MS),
            retry({ count: 1, delay: error => retryDelay(error) }),
          ),
      );
      return toDownload(response, fallbackFilename);
    } catch (error) {
      throw mapSettingsError(error);
    }
  }
}

function listParams(sortBy: string): HttpParams {
  return new HttpParams()
    .set('page', '0')
    .set('size', String(LIST_PAGE_SIZE))
    .set('sortBy', sortBy)
    .set('order', 'desc');
}

function exportParams(sortBy: string): HttpParams {
  return new HttpParams().set('sortBy', sortBy).set('order', 'desc');
}

function toFormData(write: SupplyDocumentWrite): FormData {
  const form = new FormData();
  form.append(
    'data',
    new Blob([JSON.stringify({ name: write.name, notes: write.notes })], {
      type: 'application/json',
    }),
  );
  for (const file of write.files) {
    form.append('files', file);
  }
  return form;
}

function retryDelay(error: unknown): Observable<number> {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 429) {
      const retryAfter = Number(error.headers.get('Retry-After'));
      const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
      return timer(seconds * 1000);
    }
    if (error.status >= 500 || error.status === 0) return timer(300);
  }
  return throwError(() => error);
}

function unwrapDocumentList(body: unknown): SupplyDocumentListResult {
  const page = asRecord(unwrapData(body));
  const nested = page?.['data'];
  if (!Array.isArray(nested)) throw new SettingsApiError('unexpected', 0);
  const items = nested.map(item => {
    const row = documentGrid(item);
    if (!row) throw new SettingsApiError('unexpected', 0);
    return row;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return { items, total: typeof totalRecords === 'number' ? totalRecords : items.length };
}

function unwrapDocument(body: unknown): SupplyDocumentDto {
  const document = documentDetail(unwrapData(body));
  if (!document) throw new SettingsApiError('unexpected', 0);
  return document;
}

function unwrapActionLogList(body: unknown): SupplyActionLogListResult {
  const page = asRecord(unwrapData(body));
  const nested = page?.['data'];
  if (!Array.isArray(nested)) throw new SettingsApiError('unexpected', 0);
  const items = nested.map(item => {
    const row = actionLogRow(item);
    if (!row) throw new SettingsApiError('unexpected', 0);
    return row;
  });
  const pagination = asRecord(page?.['pagination']);
  const totalRecords = pagination?.['totalRecords'];
  return { items, total: typeof totalRecords === 'number' ? totalRecords : items.length };
}

function unwrapActionLogFilters(body: unknown): SupplyActionLogFilters {
  const data = asRecord(unwrapData(body)) ?? {};
  return {
    actions: filterOptions(data['actions']),
    users: filterOptions(data['users']),
    devices: filterOptions(data['devices']),
    browsers: filterOptions(data['browsers']),
  };
}

function documentGrid(value: unknown): SupplyDocumentGridDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = text(record, 'uuid');
  const name = text(record, 'name');
  const files = record['files'];
  const notes = optionalText(record, 'notes');
  if (!uuid || !name || typeof files !== 'number' || notes === undefined) return null;
  return { uuid, name, files, notes };
}

function documentDetail(value: unknown): SupplyDocumentDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = text(record, 'uuid');
  const name = text(record, 'name');
  const createdAt = text(record, 'createdAt');
  const updatedAt = text(record, 'updatedAt');
  const files = documentFiles(record['files']);
  const notes = optionalText(record, 'notes');
  if (!uuid || !name || !createdAt || !updatedAt || !files || notes === undefined) return null;
  return { uuid, name, files, notes, createdAt, updatedAt };
}

function documentFiles(value: unknown): SupplyDocumentFileDto[] | null {
  if (!Array.isArray(value)) return null;
  const files: SupplyDocumentFileDto[] = [];
  for (const item of value) {
    const record = asRecord(item);
    if (!record) return null;
    const uuid = text(record, 'uuid');
    const name = text(record, 'name');
    const contentType = text(record, 'contentType');
    const url = text(record, 'url');
    const size = record['size'];
    if (!uuid || !name || !contentType || !url || typeof size !== 'number') return null;
    files.push({ uuid, name, contentType, size, url });
  }
  return files;
}

function actionLogRow(value: unknown): SupplyActionLogDto | null {
  const record = asRecord(value);
  if (!record) return null;
  const uuid = text(record, 'uuid');
  const dateTime = text(record, 'dateTime');
  const action = text(record, 'action');
  const user = text(record, 'user');
  if (!uuid || !dateTime || !action || !user) return null;
  return {
    uuid,
    dateTime,
    action,
    user,
    device: text(record, 'device') ?? '',
    browser: text(record, 'browser') ?? '',
    ip: text(record, 'ip') ?? '',
    location: text(record, 'location') ?? '',
  };
}

function filterOptions(value: unknown): SupplyActionLogFilterOption[] {
  if (!Array.isArray(value)) return [];
  const options: SupplyActionLogFilterOption[] = [];
  for (const item of value) {
    const record = asRecord(item);
    if (!record) continue;
    const label = text(record, 'name') ?? text(record, 'value') ?? text(record, 'label');
    if (!label) continue;
    const id = text(record, 'uuid') ?? text(record, 'value') ?? label;
    options.push({ id, label, value: label });
  }
  return options;
}

function optionalText(record: Record<string, unknown>, key: string): string | null | undefined {
  if (!(key in record) || record[key] == null) return null;
  return typeof record[key] === 'string' ? record[key] : undefined;
}

function text(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function toDownload(
  response: HttpResponse<Blob>,
  fallbackFilename: string,
): SupplyRecordDownload {
  if (!response.body) throw new SettingsApiError('unexpected', 0);
  return {
    blob: response.body,
    filename:
      contentDispositionFilename(response.headers.get('Content-Disposition')) ??
      fallbackFilename,
  };
}

function contentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
}
